import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Pagination,
  Select,
  Spinner,
  Table,
  TextInput,
} from "flowbite-react";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import moment from "moment";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
};

const formatInterval = (months) => {
  const raw = Number(months);
  if (!Number.isFinite(raw) || raw <= 0) return "-";
  if (raw === 1) return "Mensal";
  if (raw === 3) return "Trimestral";
  if (raw === 12) return "Anual";
  return `${raw} meses`;
};

const formatExpiration = (date) => {
  if (!date) return "—";
  const parsed = moment(date);
  if (!parsed.isValid()) return "—";
  return parsed.format("DD/MM/YYYY");
};

const TRIAL_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "trial", label: "Em teste" },
  { value: "paid", label: "Contratado" },
];

const PER_PAGE_OPTIONS = [10, 20, 50];

export default function CompaniesOverviewPage() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [trialFilter, setTrialFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 450);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, planFilter, trialFilter, itemsPerPage]);

  const {
    data: overview,
    isLoading,
    error,
  } = useQuery(
    [
      "companies-overview",
      searchQuery,
      planFilter,
      trialFilter,
      currentPage,
      itemsPerPage,
    ],
    async () => {
      const params = {
        page: currentPage,
        itemsPerPage,
      };
      if (searchQuery) params.search = searchQuery;
      if (planFilter) params.planId = planFilter;
      if (trialFilter !== "all") params.trialStatus = trialFilter;

      const response = await axios.get(ADMIN.GET_COMPANIES_OVERVIEW, {
        params,
      });
      return response.data?.data || null;
    },
    { keepPreviousData: true, staleTime: 5 * 60 * 1000 }
  );

  const totals = overview?.totals ?? {
    companies: 0,
    funnels: 0,
    teams: 0,
    users: 0,
  };
  const companies = overview?.companies ?? [];
  const plans = overview?.plans ?? [];
  const pagination = overview?.pagination ?? {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage,
  };

  const planOptions = useMemo(
    () =>
      plans
        .map((plan) => ({
          value: String(plan.id),
          label: plan.name || plan.code || `Plano ${plan.id}`,
        }))
        .filter((plan) => plan.value),
    [plans]
  );

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-100 text-orange-800">
            <HiOutlineOfficeBuilding className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
              Empresas Relinq
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">Clientes</h1>
          </div>
        </div>
      </header>

      <div className="space-y-4 p-3 shadow-sm rounded-md bg-white">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <TextInput
            placeholder="Buscar cliente, e-mail ou documento"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            type="search"
          />
          <Select
            value={planFilter}
            onChange={(event) => setPlanFilter(event.target.value)}
          >
            <option value="">Todos os planos</option>
            {planOptions.map((plan) => (
              <option key={plan.value} value={plan.value}>
                {plan.label}
              </option>
            ))}
          </Select>
          <Select
            value={trialFilter}
            onChange={(event) => setTrialFilter(event.target.value)}
          >
            {TRIAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            value={String(itemsPerPage)}
            onChange={(event) => setItemsPerPage(Number(event.target.value))}
          >
            {PER_PAGE_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value} por página
              </option>
            ))}
          </Select>
        </div>
        {overview && (
          <p className="text-sm text-gray-500">
            Mostrando {companies.length} de {pagination.totalCount} clientes
            encontrados
          </p>
        )}
      </div>

      <div className="shadow-sm rounded-md bg-white p-3">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Spinner color="info" />
            Carregando informações das empresas...
          </div>
        ) : error ? (
          <Alert color="failure">
            <span className="font-semibold">
              Não foi possível carregar os dados.
            </span>{" "}
            Tente novamente mais tarde.
          </Alert>
        ) : companies.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50 p-6 text-center text-sm text-orange-600">
            Nenhuma empresa encontrada com os filtros selecionados.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[720px]">
                <Table.Head>
                  <Table.HeadCell className="text-gray-500 uppercase text-[11px] tracking-wide">
                    Empresa
                  </Table.HeadCell>
                  <Table.HeadCell className="text-gray-500 uppercase text-[11px] tracking-wide">
                    Plano
                  </Table.HeadCell>
                  <Table.HeadCell className="text-gray-500 uppercase text-[11px] tracking-wide">
                    Expiração
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right text-gray-500 uppercase text-[11px] tracking-wide">
                    Funis
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right text-gray-500 uppercase text-[11px] tracking-wide">
                    Times
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right text-gray-500 uppercase text-[11px] tracking-wide">
                    Usuários
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center text-gray-500 uppercase text-[11px] tracking-wide">
                    Ações
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {companies.map((company) => {
                    const label =
                      company.socialReason ||
                      company.name ||
                      `Empresa ${company.id}`;
                    const doc = company.document || company.email;
                    const planName =
                      company.plan?.name ||
                      company.plan?.code ||
                      "Plano não informado";
                    const cycleLabel =
                      company.planCycle?.description ||
                      formatInterval(company.planCycle?.intervalMonths);
                    const planCycleInfo =
                      cycleLabel === "-"
                        ? `${formatCurrency(company.planCycle?.price)}`
                        : `${cycleLabel} · ${formatCurrency(
                            company.planCycle?.price
                          )}`;
                    const expirationDate = company.planExpirationDate
                      ? formatExpiration(company.planExpirationDate)
                      : "Sem data";
                    const isExpired =
                      company.planExpirationDate &&
                      moment().isAfter(company.planExpirationDate, "day");
                    const badgeColor = company.isTrial ? "warning" : "success";
                    const badgeLabel = company.isTrial ? "Teste" : "Contratado";

                    return (
                      <Table.Row key={company.id} className="bg-white">
                        <Table.Cell className="pr-3">
                          <p className="text-sm font-semibold text-slate-900">
                            {label}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {doc || "Sem contato"}
                          </p>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex flex-col gap-1">
                            <p className="text-sm font-semibold text-slate-900">
                              {planName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {planCycleInfo}
                            </p>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge color={badgeColor}>{badgeLabel}</Badge>
                            </div>
                            <span
                              className={`text-[11px] ${
                                isExpired ? "text-rose-600" : "text-gray-500"
                              }`}
                            >
                              {isExpired
                                ? `Expirado em ${expirationDate}`
                                : `Expira em ${expirationDate}`}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="text-right font-semibold text-gray-900">
                          {company.counts?.funnels ?? 0}
                        </Table.Cell>
                        <Table.Cell className="text-right font-semibold text-gray-900">
                          {company.counts?.teams ?? 0}
                        </Table.Cell>
                        <Table.Cell className="text-right font-semibold text-gray-900">
                          {company.counts?.users ?? 0}
                        </Table.Cell>
                        <Table.Cell className="text-center">
                          <Link
                            to={`/companies/${company.id}`}
                            className="inline-flex"
                          >
                            <Button color="gray" size="xs">
                              Ver cliente
                            </Button>
                          </Link>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-500">
                Página {pagination.currentPage} de {pagination.totalPages} ·{" "}
                {pagination.totalCount} clientes filtrados
              </p>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={(page) => setCurrentPage(page)}
                showIcons
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
